import React from 'react';
import styled from 'styled-components';

import { AuthCard, Card } from 'ts/components/Card';
import { Heading } from 'ts/components/Heading';
import LoginView from './LoginView';
import { RegisterBlock } from '../RegisterBlock/RegisterBlock';
import { AlertBox, Box, Colors, Text } from '@origin-digital/style-guide';
import { IError } from 'ts/login/interfaces';
import Loader from '../Loader';
import { NextBestAction, TargetWrapper } from '@origin-digital/personalisation';
import { Page } from '../../../components/Page';

export interface LoginFormActions {
  handleFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleUsernameOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUsernameOnBlur: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePasswordOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRememberMeChange: () => void;
}

interface LoginFormProps {
  checkingUser: boolean;
  disableUsernameIcon: boolean;
  error: IError;
  loading: boolean;
  isUserNameCheckEnabled: boolean;
  username: string;
  usernameError: string;
  password: string;
  passwordError: string;
  rememberMe: boolean;
  loginFormActions: LoginFormActions;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  checkingUser,
  disableUsernameIcon,
  error,
  loading,
  isUserNameCheckEnabled,
  username,
  usernameError,
  password,
  passwordError,
  rememberMe,
  loginFormActions,
}) => (
  <Page>
    <>
      <TargetWrapper
        mbox="OE-440-mbox"
        render={({ content }) => (
          <Card>
            <NextBestAction content={content} />
          </Card>
        )}
      />
      <AuthCard>
        <>
          <Heading text="Log in to My Account" />
          {loading ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <div>
              {error && (
                <Box marginBottom={6}>
                  <AlertBox
                    data-id={'LoginPage__errorBox'}
                    type="error"
                    heading={error.heading}
                    message={error.body}
                    markdown={true}
                  />
                </Box>
              )}
              <form onSubmit={loginFormActions.handleFormSubmit} noValidate>
                <LoginView
                  handleUsernameOnChange={loginFormActions.handleUsernameOnChange}
                  handleUsernameOnBlur={loginFormActions.handleUsernameOnBlur}
                  handlePasswordOnChange={loginFormActions.handlePasswordOnChange}
                  handleRememberMeChange={loginFormActions.handleRememberMeChange}
                  isUserNameCheckEnabled={isUserNameCheckEnabled}
                  password={password}
                  rememberMe={rememberMe}
                  username={username}
                  checkingUser={checkingUser}
                  usernameError={!error && usernameError}
                  passwordError={!error && passwordError}
                  disableUsernameIcon={
                    (!isUserNameCheckEnabled && !usernameError) || disableUsernameIcon
                  }
                />
              </form>
              <RegisterBlock />
              {isUserNameCheckEnabled && (
                <DisclaimerText markdown={true} color="grey48" size="xs">
                  This site is protected by reCAPTCHA and the Google [Privacy
                  Policy](https://policies.google.com/privacy) and [Terms of
                  Service](https://policies.google.com/terms) apply
                </DisclaimerText>
              )}
            </div>
          )}
        </>
      </AuthCard>
    </>
  </Page>
);

const DisclaimerText = styled(Text)`
  a:link,
  a:visited,
  a:hover,
  a:active {
    font-size: 12px;
    color: ${Colors.GREY48};
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
`;
